import { FC } from "react";
import { useTranslation } from "react-i18next";
import {
  Cell,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  type TooltipProps,
} from "recharts";
import { DefinitionTooltip } from "src/components/common";
import { getInstagramFollowersDefinitionInfo } from "../OrganicMediaDefinitions";
import { formatNumber, toArabicIndicDigitsInString } from "src/utils/numberFormat";
import "../../../styles/InstagramFollowersReport/InstagramGenderDemographics.scss";

export type InstagramGenderDonutSlice = {
  name: string;
  value: number;
  count: number;
  percent: number;
  color: string;
};

export type InstagramGenderCard = {
  key: string;
  title: string;
  percent: number;
  comparisonLabel: string;
  donutData: InstagramGenderDonutSlice[];
};

type InstagramGenderDemographicsProps = {
  genderCards: InstagramGenderCard[];
};

const formatComparisonLabel = (label: string | undefined, t: (_key: string) => string) => {
  const trimmed = (label ?? "").trim();
  const normalized = trimmed.toLowerCase();
  if (
    !normalized ||
    normalized === "vs last period" ||
    normalized === "vs previous period" ||
    trimmed === "مقارنة بالفترة السابقة"
  ) {
    return t("organicMedia.tooltips.vsPreviousPeriod").replace(/:$/, "");
  }
  return label as string;
};

const formatPercentValue = (value: number, language: string) => {
  const padded = Math.round(Number.isFinite(value) ? value : 0).toString().padStart(2, "0");
  return `${language?.toLowerCase().startsWith("ar") ? toArabicIndicDigitsInString(padded) : padded}%`;
};

function GenderDonutTooltip({ active, payload }: TooltipProps<number, string>) {
  const { t, i18n } = useTranslation();
  if (!active || !payload?.length) {
    return null;
  }

  const row = payload[0]?.payload as {
    name: string;
    count: number;
    percent: number;
    countFormattedValue?: string;
  };

  return (
    <div className="organic-tooltip organic-tooltip--kpi">
      <div className="organic-tooltip__bar" />
      <div className="organic-tooltip__content">
        <div className="organic-tooltip__meta-line">
          <span className="organic-tooltip__meta-label">{t("organicMedia.followers.ageRange")}</span>
          <strong className="organic-tooltip__meta-value">{row.name}</strong>
        </div>
        <div className="organic-tooltip__meta-line">
          <span className="organic-tooltip__meta-label">{t("organicMedia.tooltips.followers")}</span>
          <strong className="organic-tooltip__meta-value">
            {row.countFormattedValue ?? formatNumber(Number(row.count ?? 0), {}, i18n.language)}
          </strong>
        </div>
        <div className="organic-tooltip__meta-line">
          <span className="organic-tooltip__meta-label">{t("organicMedia.followers.percentage")}</span>
          <strong className="organic-tooltip__meta-value">
            {formatNumber(row.percent, { minimumFractionDigits: 0, maximumFractionDigits: 0 }, i18n.language)}%
          </strong>
        </div>
      </div>
    </div>
  );
}

const InstagramGenderDemographics: FC<InstagramGenderDemographicsProps> = ({ genderCards }) => {
  const { t, i18n } = useTranslation();

  return (
    <div className="organic-instagram-gender organic-content-panel" data-pdf-block="followers-gender">
      <div className="organic-content-panel__title">
        <h3>{t("organicMedia.followers.genderDemographics")}</h3>
        <DefinitionTooltip
          label={t("organicMedia.followers.genderDemographics")}
          info={getInstagramFollowersDefinitionInfo("Gender Demographics", undefined, t)}
          formulaTitle={t("organicMedia.common.formula")}
        />
      </div>
      <div className="organic-instagram-gender__content">
        {genderCards.map(group => (
          <div
            key={group.key}
            className="organic-instagram-gender__group"
          >
            <div className="organic-instagram-gender__summary">
              <h4>{group.title}</h4>
              <div className="organic-instagram-gender__percent">
                {formatNumber(Math.round(group.percent), {}, i18n.language)}%
              </div>
              <div className="organic-instagram-gender__period">
                {formatComparisonLabel(group.comparisonLabel, t)}
              </div>
            </div>
            <div className="organic-instagram-gender__donut">
              <ResponsiveContainer width="100%" height={180}>
                <PieChart>
                  <Tooltip
                    content={<GenderDonutTooltip />}
                    allowEscapeViewBox={{ x: false, y: false }}
                    wrapperStyle={{ pointerEvents: "none" }}
                  />
                  <Pie
                    data={group.donutData}
                    dataKey="value"
                    nameKey="name"
                    cx="50%"
                    cy="50%"
                    innerRadius={54}
                    outerRadius={78}
                    paddingAngle={0}
                    stroke="none"
                  >
                    {group.donutData.map(slice => (
                      <Cell key={`${group.key}-${slice.name}`} fill={slice.color} />
                    ))}
                  </Pie>
                </PieChart>
              </ResponsiveContainer>
            </div>
            <ul className="organic-instagram-gender__legend">
              {group.donutData.map(slice => (
                <li key={`${group.key}-${slice.name}`}>
                  <span
                    className="organic-instagram-gender__legend-swatch"
                    style={{ backgroundColor: slice.color }}
                  />
                  <span>
                    {i18n.language?.toLowerCase().startsWith("ar")
                      ? toArabicIndicDigitsInString(slice.name)
                      : slice.name}{" "}
                    {t("organicMedia.followers.ageYearsSuffix")}
                  </span>
                  <strong>{formatPercentValue(slice.percent, i18n.language)}</strong>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    </div>
  );
};

export default InstagramGenderDemographics;
